import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/apiService.jsx';
import './Login.css';

const ResetPassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState(location.state?.email || '');
  const [otp, setOtp] = useState(location.state?.otp || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Redirect to forgot password if no email or otp
  useEffect(() => {
    if (!email || !otp) {
      navigate('/forgot-password');
    }
  }, [email, otp, navigate]);

  const validatePassword = () => {
    const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*?])[A-Za-z\d!@#$%^&*?]{8,12}$/;
    return passwordRegex.test(newPassword);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!newPassword || !confirmPassword) {
      setError('Please enter both password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!validatePassword()) {
      setError('Password must be 8-12 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character (!@#$%^&*?)');
      return;
    }

    setLoading(true);
    try {
      await authService.resetPassword(email, newPassword, confirmPassword, otp);
      setMessage('✅ Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to reset password');
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    if (!newPassword) return null;
    
    const checks = {
      length: newPassword.length >= 8 && newPassword.length <= 12,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /\d/.test(newPassword),
      special: /[!@#$%^&*?]/.test(newPassword)
    };

    const passedChecks = Object.values(checks).filter(Boolean).length;
    
    if (passedChecks <= 2) return 'weak';
    if (passedChecks <= 4) return 'medium';
    return 'strong';
  };

  const strength = getPasswordStrength();

  return (
    <div className="login-container">
      <div className="login-split-box">
        <div className="login-card">
          <div className="login-card-content">
            <h2>Reset Password</h2>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
              Enter your new password. Make sure it's strong and secure.
            </p>

            {message && <div className="alert alert-success">{message}</div>}
            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleResetPassword}>
              <div className="input-field">
                <label className="input-label">Email</label>
                <div className="input-wrapper">
                  <input
                    type="email"
                    className="form-input"
                    value={email}
                    disabled
                    placeholder="Email"
                  />
                </div>
              </div>

              <div className="input-field">
                <label className="input-label">New Password</label>
                <div className="input-wrapper" style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className="form-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#666',
                      fontSize: '18px'
                    }}
                  >
                    {showNewPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {newPassword && (
                  <small style={{
                    display: 'block',
                    marginTop: '5px',
                    color: strength === 'weak' ? '#ff6b6b' : strength === 'medium' ? '#ffa500' : '#51cf66'
                  }}>
                    Password Strength: <strong>{strength?.toUpperCase()}</strong>
                  </small>
                )}
              </div>

              <div className="input-field">
                <label className="input-label">Confirm Password</label>
                <div className="input-wrapper" style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="form-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#666',
                      fontSize: '18px'
                    }}
                  >
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {newPassword && confirmPassword && (
                  <small style={{
                    display: 'block',
                    marginTop: '5px',
                    color: newPassword === confirmPassword ? '#51cf66' : '#ff6b6b'
                  }}>
                    {newPassword === confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </small>
                )}
              </div>

              <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
                <p style={{ fontSize: '12px', color: '#666', margin: '0 0 8px 0', fontWeight: 'bold' }}>Password Requirements:</p>
                <ul style={{ fontSize: '11px', color: '#666', margin: 0, paddingLeft: '20px' }}>
                  <li>8-12 characters long</li>
                  <li>At least one uppercase letter (A-Z)</li>
                  <li>At least one lowercase letter (a-z)</li>
                  <li>At least one number (0-9)</li>
                  <li>At least one special character (!@#$%^&*?)</li>
                </ul>
              </div>

              <button
                className="login-button"
                type="submit"
                disabled={loading || !validatePassword() || newPassword !== confirmPassword}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </form>

            <div style={{ marginTop: '20px', textAlign: 'center' }}>
              <p style={{ fontSize: '12px', color: '#999' }}>
                <a href="/login" style={{ color: '#3b82f6', textDecoration: 'none' }}>Back to Login</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
